/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../../../../../app/store';
import { snackNotifications } from '../../../notification/SnackBarUtils';
import { RuleEngine, RuleEngineData } from './ruleEngine/patientSummaryRuleEngine';
import { ruleConfig } from './ruleEngine/ruleConfig';
import { analyticsTrack } from '../../../analytics_utils';
import { DocumentVersionType } from '../../../algorithm/types';
import { contextualizationText, summaryText, topicText } from '../../../utils';
import { SummaryTemplate } from './SummaryTemplate';

type topicType = {
  title: string;
  doc_version_id: string;
  reason: { text: string; flag?: string };
  helpful?: string;
};

const initialState: {
  showSummary: boolean;
  showRegenerateSummary: boolean;
  showContextualization: boolean;
  showTopic: boolean;
  fetchingSuggestedTopics: boolean;
  fetchingSummary: boolean;
  summary: string;
  suggestedTopics: topicType[];
} = {
  showSummary: false,
  showRegenerateSummary: false,
  showContextualization: true,
  showTopic: true,
  fetchingSummary: false,
  fetchingSuggestedTopics: false,
  summary: '',
  suggestedTopics: [],
};

export const generatePatientSummaryThunk = createAsyncThunk<
  void,
  {
    data: RuleEngineData;
    documentVersion: DocumentVersionType | undefined;
    algoSelectionHTML?: string;
  },
  { state: RootState }
>('generatePatientSummaryThunk', async ({ data, documentVersion, algoSelectionHTML }, thunkAPI) => {
  try {
    const {
      user: { pearlUserInfo },
    } = thunkAPI.getState();

    analyticsTrack(
      'generate_patient_notes_summary',
      {
        documentName: documentVersion?.title,
        documentId: documentVersion?.document?.id,
        documentSlug: documentVersion?.document?.slug,
        documentVersionId: documentVersion?.id,
        slug: documentVersion?.document?.slug,
      },
      pearlUserInfo
    );

    if (pearlUserInfo?.userFlags?.patient_notes_summary) {
      const ruleEngine = new RuleEngine(ruleConfig);

      const state = await ruleEngine.execute(data);

      let summary = SummaryTemplate(documentVersion?.title!, !!algoSelectionHTML);

      summary = summary.replace('{{selectionSummary}}', algoSelectionHTML || '');
      summary = summary.replace('{{labsDoneSummary}}', state.labs.done.join('<br/>'));
      summary = summary.replace('{{labsNotDoneSummary}}', state.labs.notDone.join('<br/>'));
      summary = summary.replace('{{imagingDoneSummary}}', state.imaging.done.join('<br/>'));
      summary = summary.replace('{{imagingNotDoneSummary}}', state.imaging.notDone.join('<br/>'));
      summary = summary.replace('{{proceduresDoneSummary}}', state.procedures.done.join('<br/>'));
      summary = summary.replace('{{proceduresNotDoneSummary}}', state.procedures.notDone.join('<br/>'));

      thunkAPI.dispatch(addSummaryChunk(summary));
      thunkAPI.dispatch(setFetchingSummary(false));
    }
  } catch (err) {
    thunkAPI.getState().patientSummary.fetchingSummary = false;
    snackNotifications.error(`Failed to generate patient's summary`);
    throw err;
  }
});

export const patientSummarySlice = createSlice({
  name: 'patientSummary',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    togglePaneView: (state, action: PayloadAction<string>) => {
      const type = action.payload;
      if (type === topicText) {
        state.showTopic = !state.showTopic;
      } else if (type === contextualizationText) {
        state.showContextualization = !state.showContextualization;
      } else if (type === summaryText) {
        state.showSummary = !state.showSummary;
      } else return;
    },
    addSummaryChunk: (state, action: PayloadAction<string>) => {
      state.showSummary = true;
      state.summary += action.payload + '<br>';
      state.showRegenerateSummary = false;
    },
    setFetchingSummary: (state, action: PayloadAction<boolean>) => {
      state.fetchingSummary = action.payload;
      state.showRegenerateSummary = false;
    },
    clearSummary: (state) => {
      state.summary = '';
      state.fetchingSummary = false;
      state.showRegenerateSummary = false;
    },
    resetThreePaneViewStates: (state) => {
      state.showContextualization = true;
      state.showTopic = true;
      state.showSummary = false;
    },
    enableRegenerateSummary: (state) => {
      if (state.summary && !state.fetchingSummary) {
        state.showRegenerateSummary = true;
      }
    },
    likeSuggestion: (state, action: PayloadAction<{ id: string; helpful: string }>) => {
      state.suggestedTopics = state.suggestedTopics.map((doc) =>
        doc.doc_version_id === action.payload.id
          ? {
              ...doc,
              helpful: action.payload.helpful,
            }
          : doc
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(generatePatientSummaryThunk.pending, (state) => {
      state.fetchingSummary = true;
    });
  },
});

export const {
  likeSuggestion,
  clearSummary,
  togglePaneView,
  setFetchingSummary,
  addSummaryChunk,
  enableRegenerateSummary,
  resetThreePaneViewStates,
} = patientSummarySlice.actions;

export const selectPatientSummaryState = (state: RootState) => state.patientSummary;
export const selectCurrentPaneState = (
  state: RootState
): { topic: boolean; contextualization: boolean; summary: boolean } => ({
  topic: state.patientSummary.showTopic,
  contextualization: state.patientSummary.showContextualization,
  summary: state.patientSummary.showSummary,
});

export default patientSummarySlice.reducer;
